
import { Form, message, Spin } from "antd";
import classes from "./warrantyPeriod.module.css"
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from 'react-intl';
import { checkWarrantyPeriod, sendSystemMessage } from "../../api/warranty";
import { getCustomFormData } from "../../api/customForm";
import { getMerchantCode } from "../../components/customFormComponents/utils/commonFun";
import { Helmet } from "react-helmet";
import MainComponent from "../../components/MainComponent/mainComponent";
import { useFormRecaptcha } from "../../hooks/useFormRecaptcha";
import FormRecaptcha from "../../components/FormRecaptcha";

const WarrantyPeriod = props => {

    const { formatMessage } = useIntl();
    const [form] = Form.useForm();
    const {pathname, search} = useLocation();
    const {merchant_code} = useParams();
    const [messageApi, contextHolder] = message.useMessage();
    const [merchantCode, setMerchantCode] = useState('');
    const [languageId, setLanguageId] = useState('global');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState(false);

    const [recaptchaConfig, setRecaptchaConfig] = useState(null);
    // [reCAPTCHA] reCAPTCHA config is read from the public form schema.
    const recaptcha = useFormRecaptcha(recaptchaConfig);

    const handleSendMessage = useCallback((values) => {
        sendSystemMessage({
            type: "query-form",
            id: values.email,
            content: values,
            merchant: merchant_code === 'uat_test' ? 'demo' : merchant_code,
            language: languageId
        }, merchantCode).then(res=>{
            setLoading(false);
            if(res?.status == '200') {
                setSuccessMessage(formatMessage({
                    id: "warrantyPeriod.sendSuccessfulLabel", 
                    defaultMessage: "An email has been successfully sent to the provided email address. Please check your inbox."
                }))
            }
            else {
                setErrorMessage(res.message);
            }
        }).catch(error=>{
            setErrorMessage(error.response?.data?.message || error.message);
            setSuccessMessage(null);
            setLoading(false);
        })
    }, [formatMessage, languageId, merchantCode, merchant_code])

    const handleSubmitForm = useCallback(async (values)=>{
        setLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        // [reCAPTCHA] obtain proof (v2/v3) before sending the submission.
        let recaptchaProof = null;
        try {
            recaptchaProof = await recaptcha.getSubmitProof();
        } catch (e) {
            setLoading(false);
            messageApi.open({
                type: 'error',
                content: e?.message || 'reCAPTCHA verification failed',
            });
            return;
        }

        const body = {
            ...values,
            ...(recaptchaProof ? { recaptcha: recaptchaProof } : {}),
        };

        try {
            let res = await checkWarrantyPeriod(body, merchantCode);

            // [reCAPTCHA] v3 low score -> backend requests a v2 challenge.
            if(res?.errorCode === 'RECAPTCHA_CHALLENGE_REQUIRED' && res?.recaptchaChallenge) {
                recaptcha.renderChallenge(res.recaptchaChallenge);
                if(res.recaptchaChallenge.widget === 'invisible') {
                    const v2Proof = await recaptcha.getSubmitProof();
                    res = await checkWarrantyPeriod({ ...body, recaptcha: v2Proof }, merchantCode);
                } else {
                    setLoading(false);
                    messageApi.open({
                        type: 'warning',
                        content: 'Please complete the reCAPTCHA verification and submit again.',
                    });
                    return;
                }
            }

            if(res && res.emailAddress) {
                // [reCAPTCHA] reset v2 widget and clear fallback challenge after success.
                recaptcha.clearChallenge();
                handleSendMessage(values);
            }
            else {
                const errCode = res?.errorCode;
                if(errCode === 'RECAPTCHA_INVALID' || errCode === 'RECAPTCHA_REQUIRED') {
                    recaptcha.reset();
                }
                setErrorMessage(res?.message || formatMessage({
                    id: "warrantyPeriod.checkNoRecordLabel", 
                    defaultMessage: "Sorry, no record was found!"
                }));
                setLoading(false);
            }
        } catch (error) {
            messageApi.open({
                type: 'error',
                content: error.response?.data?.message || error.message
            });
            setErrorMessage(error.response?.data?.message || error.message);
            setLoading(false);
        }
    }, [formatMessage, handleSendMessage, merchantCode, messageApi, recaptcha])

    useEffect(()=>{
        if(pathname.includes('/check_period/warranty') && merchant_code) {
            const code = merchant_code === 'uat_test' ? '' : `api/merchant/${merchant_code}/warranty/`;
            const params = new URLSearchParams(search);
            const language_id = params?.get('language') || 'global';
            const formCode = params?.get('code') || 'default';
            setLanguageId(language_id)
            setMerchantCode(code);

            // [reCAPTCHA] fetch the public form schema to obtain reCAPTCHA config.
            const mCode = getMerchantCode(merchant_code, 'form');
            getCustomFormData({
                formCode: formCode,
                locale: 'global'
            }, mCode ? mCode : merchant_code, mCode ? null : 'form').then(res => {
                if(res?.status === '200') {
                    setRecaptchaConfig(res.data?.recaptcha || null);
                }
            }).catch(() => {
                // If schema fetch fails, recaptcha stays disabled (no blocking).
                setRecaptchaConfig(null);
            });
        }
    }, [pathname, search, merchant_code])

    const FormContent = useMemo(()=>{
        return <div className={classes.root}>
            <h1>
                <FormattedMessage id={'warrantyPeriod.checkTitle'} defaultMessage={'Check Warranty Status'} />
            </h1>
            {
                errorMessage ? <p className={classes.error_msg}>{errorMessage}</p> : null
            }
            {
                successMessage ? <p className={classes.success_msg}>{successMessage}</p> : null
            }
            <p>
                <FormattedMessage id={'warrantyPeriod.checkDescription'} defaultMessage={'Please enter your email and serial number to check your product warranty status.'} />
            </p>
            {contextHolder}
            <Spin spinning={loading}>
                <Form className={classes.modalForm} form={form} name="group_form" onFinish={handleSubmitForm} labelCol={{span: 16}}
                    layout="vertical"
                >
                    <Form.Item label={formatMessage({
                        id: "warrantyPeriod.emailLabel", 
                        defaultMessage: "Email"
                    })+": "} name="email" rules={[
                        { required: true, message: formatMessage({
                            id: "form.isRequired", 
                            defaultMessage: "{name} is required!"
                        }, {
                            name: formatMessage({
                                id: "warrantyPeriod.emailLabel", 
                                defaultMessage: "Email"
                            })
                        }) },
                        {
                            pattern: /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                            message: formatMessage({
                                id: "form.isEmail", 
                                defaultMessage: "Please enter a valid email account"
                            })
                        }
                    ]}>
                        <input type="text" placeholder={formatMessage({
                            id: "warrantyPeriod.emailPlaceholder", 
                            defaultMessage: "Enter Email"
                        })}
                            onChange={(e)=>{setErrorMessage(null); setSuccessMessage(null)}}
                        />
                    </Form.Item>
                    <Form.Item label={formatMessage({
                        id: "warrantyPeriod.serialNumberLabel", 
                        defaultMessage: "Serial Number"
                    })+": "} name="serialNumber" rules={[
                        { required: true, message: formatMessage({
                            id: "form.isRequired", 
                            defaultMessage: "{name} is required!"
                        }, {
                            name: formatMessage({
                                id: "warrantyPeriod.serialNumberLabel", 
                                defaultMessage: "Serial Number"
                            })
                        }) },
                        {
                            pattern: /^.{13}$/,
                            message: formatMessage({
                                id: "form.isSerialNumber", 
                                defaultMessage: "Please put 13 characters into {name}."
                            }, {
                                name: formatMessage({
                                    id: "warrantyPeriod.serialNumberLabel", 
                                    defaultMessage: "Serial Number"
                                })
                            })
                        }
                    ]}>
                        <input type="text" placeholder={formatMessage({
                            id: "warrantyPeriod.serialNumberPlaceholder", 
                            defaultMessage: "Enter Serial Number"
                        })}
                            onChange={(e)=>{setErrorMessage(null); setSuccessMessage(null)}}
                        />
                    </Form.Item>
                    <Form.Item>
                        <FormRecaptcha
                            v2Ref={recaptcha?.v2Ref}
                            v2Config={recaptcha?.v2Config}
                            v3Config={recaptcha?.v3Config}
                            showV2Widget={recaptcha?.showV2Widget}
                            showV3Widget={recaptcha?.showV3Widget}
                            onV3Ready={recaptcha?.onV3Ready}
                            onV3Error={recaptcha?.onV3Error}
                            onV2Ready={recaptcha?.onV2Ready}
                            onV2Change={recaptcha?.onV2Change}
                            onV2Expired={recaptcha?.onV2Expired}
                            onV2Errored={recaptcha?.onV2Errored}
                            scriptReady={recaptcha?.scriptReady}
                            scriptError={recaptcha?.scriptError}
                            v2Error={recaptcha?.v2Error}
                            v2Expired={recaptcha?.v2Expired}
                            v2TokenReady={recaptcha?.v2TokenReady}
                            style={{ marginBottom: '12px' }}
                        />
                        <div className={classes.formButton}>
                            <button type="submit">
                                <FormattedMessage id={'warrantyPeriod.checkButton'} defaultMessage={'Check'} />
                            </button>
                        </div>
                    </Form.Item>
                </Form>
            </Spin>
        </div>
    }, [contextHolder, errorMessage, form, formatMessage, handleSubmitForm, loading, successMessage, recaptcha])

    function isIframe() {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true; // 跨域访问报错时，也说明在 iframe 中
        }
    }

    return <>
        <Helmet>
            <title>Check Warranty Status</title>
            <meta name="description" content="Check Warranty Status" />
            <link rel="icon" href="https://www.gaxas.ai/api/media/file/favicon-1.svg"/>
        </Helmet>
        
        <MainComponent children={FormContent} isIframe={isIframe()}/>
    </>
}

export default WarrantyPeriod;